/**
 * GRACE PERIOD BANNER
 * 
 * Warning banner shown to users whose subscription has been cancelled
 * and who are in their 30-day grace period.
 * 
 * Shows:
 * - Days remaining in grace period
 * - What happens when it expires (revert to free, photos at risk)
 * - Reactivate button (opens Stripe customer portal)
 * - Download photos button (photo exporter - future feature)
 * 
 * Usage:
 *   <GracePeriodBanner />
 *   
 * Place this in your main layout (e.g., App.tsx or a layout wrapper)
 * so it appears on every page during the grace period.
 */

import { useState } from 'react';
import { AlertTriangle, X, CreditCard, Download, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useStorageUsage } from '../hooks/useStorageUsage';
import { auth } from '../firebase/config';
import { formatBytes } from '../constants/storageTiers';

// Cloud Function URL for creating Stripe portal session
const PORTAL_FUNCTION_URL = import.meta.env.VITE_FIREBASE_PORTAL_URL || 
  'https://us-central1-polish-all-the-things.cloudfunctions.net/createPortalSession';

export default function GracePeriodBanner() {
  const { subscription, usedBytes } = useStorageUsage();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  // Only show during grace period
  if (!subscription || subscription.status !== 'grace_period') return null;
  if (dismissed) return null;

  // Calculate days remaining
  const gracePeriodEnds = subscription.gracePeriodEnds 
    ? new Date(subscription.gracePeriodEnds) 
    : null;
  
  if (!gracePeriodEnds) return null;

  const now = new Date();
  const msRemaining = gracePeriodEnds.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));

  // Don't show if grace period already expired (cleanup function handles that)
  if (daysRemaining <= 0) return null;

  // Determine urgency level
  const isUrgent = daysRemaining <= 7;
  const isCritical = daysRemaining <= 3;

  // Free tier limit for comparison
  const FREE_LIMIT = 50 * 1024 * 1024; // 50 MB
  const isOverFreeLimit = usedBytes > FREE_LIMIT;
  const excessBytes = Math.max(0, usedBytes - FREE_LIMIT);

  // Handle reactivate click - open Stripe portal
  const handleReactivate = async () => {
    const user = auth.currentUser;
    if (!user) return;
    
    setLoadingPortal(true);
    try {
      const response = await fetch(PORTAL_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          returnUrl: window.location.href,
        }),
      });
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <div className={`
      border-b shadow-lg transition-colors
      ${isCritical
        ? 'bg-gradient-to-r from-red-600 to-red-500 border-red-400'
        : isUrgent
          ? 'bg-gradient-to-r from-amber-600 to-orange-500 border-amber-400'
          : 'bg-gradient-to-r from-amber-500 to-orange-400 border-amber-300'
      }
    `}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        {/* Main row */}
        <div className="flex items-center gap-3">
          <div className={`flex-shrink-0 ${isCritical ? 'animate-pulse' : ''}`}>
            {isCritical ? (
              <AlertTriangle className="w-5 h-5 text-white" />
            ) : (
              <Clock className="w-5 h-5 text-white" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium font-body text-white">
              Your subscription has ended.{' '}
              <span className="font-bold">
                {daysRemaining === 1
                  ? '1 day remaining'
                  : `${daysRemaining} days remaining`
                }
              </span>
              {' '}to reactivate or download your photos.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Expand/collapse for details */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 text-white/70 hover:text-white transition-default md:hidden"
              aria-label="Toggle details"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Reactivate button - always visible */}
            <button
              onClick={handleReactivate}
              disabled={loadingPortal}
              className="px-3 py-1.5 bg-white text-amber-700 rounded-lg text-xs sm:text-sm font-bold hover:bg-white/90 transition-default disabled:opacity-50 flex items-center gap-1.5 shadow-md"
            >
              {loadingPortal ? (
                <span className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
              ) : (
                <CreditCard className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">Reactivate</span>
            </button>

            {/* Dismiss */}
            <button
              onClick={() => setDismissed(true)}
              className="p-1.5 text-white/50 hover:text-white transition-default"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Expanded details (always visible on desktop, toggle on mobile) */}
        <div className={`mt-2 pl-8 ${expanded ? 'block' : 'hidden md:block'}`}>
          {isOverFreeLimit && (
            <p className="text-xs text-white/90 font-body mb-2">
              You&apos;re currently using <strong>{formatBytes(usedBytes)}</strong> of storage.
              The free tier allows <strong>50 MB</strong>.
              {' '}<strong>{formatBytes(excessBytes)}</strong> of photos may be removed after the grace period.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Photo export button - placeholder for now */}
            <button
              onClick={() => {
                // TODO: Navigate to photo exporter when built
                // For now, show a message or navigate to settings
                alert('Photo export coming soon! For now, you can download individual photos from your collection.');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium transition-default"
            >
              <Download className="w-3.5 h-3.5" />
              Download Photos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * EXPIRED SUBSCRIPTION BANNER
 * 
 * Shown after the grace period has fully expired and user is back on free tier.
 * Less urgent than the grace period banner — just informational.
 */
export function ExpiredSubscriptionBanner() {
  const { subscription, usedBytes } = useStorageUsage();
  const [dismissed, setDismissed] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  // Only show for expired subscriptions
  if (!subscription || subscription.status !== 'expired') return null;
  if (dismissed) return null;

  const FREE_LIMIT = 50 * 1024 * 1024;
  const isOverFreeLimit = usedBytes > FREE_LIMIT;

  const handleReactivate = async () => {
    const user = auth.currentUser;
    if (!user) return;
    
    setLoadingPortal(true);
    try {
      const response = await fetch(PORTAL_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          returnUrl: window.location.href,
        }),
      });
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-slate-700 to-slate-600 border-b border-slate-500 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
        <p className="text-xs text-white font-body flex-1">
          Your subscription has expired.
          {isOverFreeLimit && ' New photo uploads are blocked until you upgrade.'}
          {' '}Resubscribe anytime to unlock full storage.
        </p>
        <button
          onClick={handleReactivate}
          disabled={loadingPortal}
          className="px-3 py-1 bg-white text-slate-700 rounded-lg text-xs font-bold hover:bg-white/90 transition-default flex items-center gap-1.5 shadow-md"
        >
          <CreditCard className="w-3 h-3" />
          Resubscribe
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-white/50 hover:text-white transition-default"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
